// /app/frontend/src/components/PhotoWallManager.jsx
/**
 * PhotoWallManager - Component for managing the photo wall in DrawEditor
 * - Configure photo wall settings
 * - View/moderate uploaded photos
 * - Download all photos
 */
import React, { useState, useEffect } from 'react';
import { 
  Camera, Eye, EyeOff, Trash2, Download, Settings, QrCode,
  Video, Image as ImageIcon, Users, RefreshCw, Check, X,
  ExternalLink, Copy, Clock
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import api from '../lib/api';
import { getImageUrl } from '../lib/utils';

export default function PhotoWallManager({ drawId, drawTitle }) {
  const [config, setConfig] = useState({
    enabled: false,
    require_approval: false,
    allow_videos: true,
    allow_messages: true,
    max_photos_per_participant: 5,
    instructions: ""
  });
  const [photos, setPhotos] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  
  const photoWallUrl = `${window.location.origin}/draw/${drawId}/photos`;
  const publicWallUrl = `${window.location.origin}/draw/${drawId}/wall`;

  useEffect(() => {
    loadData();
  }, [drawId]);

  useEffect(() => {
    // Auto-refresh photos every 10 seconds when enabled
    if (config.enabled) {
      const interval = setInterval(loadPhotos, 10000);
      return () => clearInterval(interval);
    }
  }, [config.enabled, drawId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [configRes, photosRes, statsRes] = await Promise.all([
        api.get(`/draws/${drawId}/photo-wall/config`),
        api.get(`/draws/${drawId}/photo-wall/photos`),
        api.get(`/draws/${drawId}/photo-wall/stats`)
      ]);
      setConfig(configRes.data);
      setPhotos(photosRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading photo wall data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPhotos = async () => {
    try {
      const [photosRes, statsRes] = await Promise.all([
        api.get(`/draws/${drawId}/photo-wall/photos`),
        api.get(`/draws/${drawId}/photo-wall/stats`)
      ]);
      setPhotos(photosRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error refreshing photos:', error);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put(`/draws/${drawId}/photo-wall/config`, config);
      toast.success('Configuration sauvegardée');
    } catch (error) {
      toast.error('Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const togglePhotoVisibility = async (photo) => {
    try {
      await api.put(`/draws/${drawId}/photo-wall/photos/${photo.id}`, {
        hidden: !photo.hidden
      });
      setPhotos(photos.map(p => 
        p.id === photo.id ? { ...p, hidden: !p.hidden } : p
      ));
      toast.success(photo.hidden ? 'Photo visible' : 'Photo masquée');
    } catch (error) {
      toast.error('Erreur de mise à jour');
    }
  };

  const deletePhoto = async (photoId) => {
    try {
      await api.delete(`/draws/${drawId}/photo-wall/photos/${photoId}`);
      setPhotos(photos.filter(p => p.id !== photoId));
      setSelectedPhoto(null);
      toast.success('Photo supprimée');
    } catch (error) {
      toast.error('Erreur de suppression');
    }
  };

  const downloadAll = async () => {
    setDownloading(true);
    try {
      const response = await api.get(`/draws/${drawId}/photo-wall/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `photos_${drawTitle || 'tirage'}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Téléchargement commencé');
    } catch (error) {
      toast.error('Erreur de téléchargement');
    } finally {
      setDownloading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(photoWallUrl);
    toast.success('Lien copié !');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      {/* Header Stats */}
      {stats && config.enabled && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{stats.total}</div>
              <div className="text-sm opacity-80">Total</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{stats.visible}</div>
              <div className="text-sm opacity-80">Visibles</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{stats.hidden}</div>
              <div className="text-sm opacity-80">Masquées</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{stats.videos}</div>
              <div className="text-sm opacity-80">Vidéos</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-pink-500 to-pink-600 text-white">
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{stats.unique_participants}</div>
              <div className="text-sm opacity-80">Participants</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Configuration du mur photos
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Activer le mur photos</Label>
              <p className="text-sm text-muted-foreground">
                Les participants pourront partager leurs photos
              </p>
            </div>
            <Switch
              checked={config.enabled}
              onCheckedChange={(checked) => setConfig({...config, enabled: checked})}
            />
          </div>

          {config.enabled && (
            <>
              <div className="border-t pt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Autoriser les vidéos</Label>
                    <p className="text-sm text-muted-foreground">Vidéos de 5 secondes max, sans son</p>
                  </div>
                  <Switch
                    checked={config.allow_videos}
                    onCheckedChange={(checked) => setConfig({...config, allow_videos: checked})}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Autoriser les messages</Label>
                    <p className="text-sm text-muted-foreground">Texte affiché sur la photo</p>
                  </div>
                  <Switch
                    checked={config.allow_messages}
                    onCheckedChange={(checked) => setConfig({...config, allow_messages: checked})}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Modération préalable</Label>
                    <p className="text-sm text-muted-foreground">Les photos doivent être approuvées avant affichage</p>
                  </div>
                  <Switch
                    checked={config.require_approval}
                    onCheckedChange={(checked) => setConfig({...config, require_approval: checked})}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Photos max par participant</Label>
                    <Input
                      type="number"
                      min={1}
                      max={20}
                      value={config.max_photos_per_participant}
                      onChange={(e) => setConfig({...config, max_photos_per_participant: parseInt(e.target.value) || 5})}
                    />
                  </div>
                </div>

                {/* Instructions for participants */}
                <div>
                  <Label>Consignes pour les participants</Label>
                  <p className="text-sm text-muted-foreground mb-2">
                    Ce message sera affiché avant le formulaire d'identification
                  </p>
                  <Textarea
                    placeholder="Ex: Partagez vos meilleurs moments de la soirée ! Les photos inappropriées seront supprimées."
                    value={config.instructions || ''}
                    onChange={(e) => setConfig({...config, instructions: e.target.value})}
                    className="resize-none"
                    rows={3}
                    maxLength={500}
                  />
                  <p className="text-xs text-muted-foreground text-right mt-1">
                    {(config.instructions || '').length}/500
                  </p>
                </div>
              </div>

              {/* QR Code & Link */}
              <div className="border-t pt-4">
                <Label className="mb-2 block">Lien de partage</Label>
                <div className="flex gap-2">
                  <Input 
                    value={photoWallUrl} 
                    readOnly 
                    className="flex-1 bg-muted"
                  />
                  <Button variant="outline" onClick={copyLink}>
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" onClick={() => setShowQRCode(true)}>
                    <QrCode className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" onClick={() => window.open(photoWallUrl, '_blank')}>
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
                
                {/* Public Wall Link for Social Media */}
                <div className="mt-4 pt-4 border-t border-dashed">
                  <Label className="mb-2 block text-muted-foreground">Lien du mur (lecture seule - pour réseaux sociaux)</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={publicWallUrl} 
                      readOnly 
                      className="flex-1 bg-muted text-sm"
                    />
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        navigator.clipboard.writeText(publicWallUrl);
                        toast.success('Lien du mur copié !');
                      }}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" onClick={() => window.open(publicWallUrl, '_blank')}>
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
            <Button onClick={saveConfig} disabled={saving}>
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Photos Grid */}
      {config.enabled && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Photos partagées ({photos.length})
            </CardTitle>
            {photos.length > 0 && (
              <Button variant="outline" onClick={downloadAll} disabled={downloading}>
                <Download className="h-4 w-4 mr-2" />
                {downloading ? 'Téléchargement...' : 'Tout télécharger'}
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {photos.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Camera className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Aucune photo pour le moment</p>
                <p className="text-sm mt-1">Partagez le QR code pour que les participants puissent envoyer leurs photos</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {photos.map(photo => (
                  <div 
                    key={photo.id}
                    className={`group relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                      photo.hidden ? 'border-red-300 opacity-60' : 'border-transparent'
                    }`}
                    onClick={() => setSelectedPhoto(photo)}
                  >
                    {photo.media_type === 'video' ? (
                      <video 
                        src={getImageUrl(photo.url)} 
                        className="w-full h-full object-cover"
                        muted
                      />
                    ) : (
                      <img 
                        src={getImageUrl(photo.url)} 
                        alt={photo.pseudo}
                        className="w-full h-full object-cover"
                      />
                    )}
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="absolute bottom-0 left-0 right-0 p-2">
                        <p className="text-white text-sm font-medium truncate">{photo.pseudo}</p>
                        {photo.message && (
                          <p className="text-white/80 text-xs truncate">{photo.message}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Hidden badge */}
                    {photo.hidden && (
                      <div className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded">
                        <EyeOff className="h-3 w-3" />
                      </div>
                    )}
                    
                    {/* Video badge */}
                    {photo.media_type === 'video' && (
                      <div className="absolute top-2 left-2 bg-black/50 text-white p-1 rounded">
                        <Video className="h-3 w-3" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* QR Code Dialog */}
      <Dialog open={showQRCode} onOpenChange={setShowQRCode}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>QR Code - Mur Photos</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center py-6">
            <div className="bg-white p-4 rounded-lg">
              <QRCodeSVG 
                value={photoWallUrl} 
                size={200}
                level="H"
              />
            </div>
            <p className="text-sm text-muted-foreground mt-4 text-center">
              Scannez ce QR code pour partager vos photos
            </p>
            <Input 
              value={photoWallUrl} 
              readOnly 
              className="mt-4 text-center text-sm"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQRCode(false)}>Fermer</Button>
            <Button onClick={copyLink}>
              <Copy className="h-4 w-4 mr-2" />
              Copier le lien
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photo Detail Dialog */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Photo de {selectedPhoto?.pseudo}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Media */}
            <div className="flex justify-center bg-black/5 rounded-lg overflow-hidden">
              {selectedPhoto?.media_type === 'video' ? (
                <video 
                  src={getImageUrl(selectedPhoto?.url)} 
                  className="max-h-[50vh] rounded"
                  controls
                  autoPlay
                  muted
                  loop
                />
              ) : (
                <img 
                  src={getImageUrl(selectedPhoto?.url)} 
                  alt={selectedPhoto?.pseudo}
                  className="max-h-[50vh] rounded"
                />
              )}
            </div>
            
            {/* Info */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <Label className="text-muted-foreground">Auteur</Label>
                <p className="font-medium">{selectedPhoto?.first_name} ({selectedPhoto?.pseudo})</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Date</Label>
                <p className="font-medium flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {selectedPhoto?.created_at ? new Date(selectedPhoto.created_at).toLocaleString('fr-FR') : '-'}
                </p>
              </div>
              {selectedPhoto?.email && (
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p className="font-medium">{selectedPhoto.email}</p>
                </div>
              )}
              {selectedPhoto?.phone && (
                <div>
                  <Label className="text-muted-foreground">Téléphone</Label>
                  <p className="font-medium">{selectedPhoto.phone}</p>
                </div>
              )}
            </div>
            
            {selectedPhoto?.message && (
              <div>
                <Label className="text-muted-foreground">Message</Label>
                <p className="mt-1 p-3 bg-muted rounded-lg">{selectedPhoto.message}</p>
              </div>
            )}
          </div>
          <DialogFooter className="flex justify-between">
            <Button 
              variant="destructive" 
              onClick={() => deletePhoto(selectedPhoto?.id)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Supprimer
            </Button>
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => togglePhotoVisibility(selectedPhoto)}
              >
                {selectedPhoto?.hidden ? (
                  <>
                    <Eye className="h-4 w-4 mr-2" />
                    Rendre visible
                  </>
                ) : (
                  <>
                    <EyeOff className="h-4 w-4 mr-2" />
                    Masquer
                  </>
                )}
              </Button>
              <Button variant="outline" onClick={() => setSelectedPhoto(null)}>
                Fermer
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
